/** A company's public profile: its lifetime spend and everything it has live. */

import { Link, useParams } from 'react-router-dom';
import type { ReactNode } from 'react';

import { Leaderboard } from '../components/Leaderboard';
import { Money } from '../components/Money';
import { BoardSkeleton, EmptyState, ErrorNotice, MissingState, isMissing } from '../components/Status';
import { CompanyMark } from '../components/ui';
import { useCompany } from '../lib/queries';

export function CompanyPage(): ReactNode {
  const params = useParams<{ slug: string }>();
  const query = useCompany(params.slug);

  if (query.isLoading) {
    return (
      <div className="inner" style={{ paddingTop: 32 }}>
        <BoardSkeleton rows={4} />
      </div>
    );
  }
  if (query.isError) {
    return (
      <div className="inner" style={{ paddingTop: 32 }}>
        {isMissing(query.error) ? (
          <MissingState what="company" />
        ) : (
          <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
        )}
      </div>
    );
  }
  if (query.data === undefined) return null;

  const { company, jobs } = query.data;
  const details = [
    company.industry,
    company.hqLocation,
    company.sizeBand === undefined ? undefined : `${company.sizeBand} people`,
  ]
    .filter((part): part is string => part !== undefined && part !== '')
    .join(' · ');

  return (
    <>
      <div style={{ borderBottom: '1px solid var(--hairline)', background: 'var(--canvas-soft)' }}>
        <div className="inner" style={{ paddingTop: 40, paddingBottom: 36 }}>
          <nav className="crumbs" aria-label="Breadcrumb">
            <Link to="/">Leaderboard</Link>
            <span className="sep">/</span>
            <span>{company.name}</span>
          </nav>

          <div className="row" style={{ gap: 16, marginTop: 20, alignItems: 'center' }}>
            <CompanyMark company={company} size="lg" />
            <div>
              <h1 style={{ fontSize: 32 }}>{company.name}</h1>
              {details === '' ? null : (
                <p className="muted small" style={{ marginTop: 6 }}>
                  {details}
                </p>
              )}
            </div>
          </div>

          {company.blurb === undefined ? null : (
            <p className="lede" style={{ marginTop: 18, maxWidth: '62ch', fontSize: 16 }}>
              {company.blurb}
            </p>
          )}

          <div className="statgrid" style={{ marginTop: 26 }}>
            <div className="stat">
              <span className="eyebrow">Total paid</span>
              <div className="value money" style={{ color: 'var(--indigo-deep)' }}>
                <Money cents={company.totalSpendCents} />
              </div>
              <div className="note">Across every listing this company has run</div>
            </div>
            <div className="stat">
              <span className="eyebrow">Live listings</span>
              <div className="value money">{company.activeJobCount}</div>
              <div className="note">Ranked on the board</div>
            </div>
            <div className="stat">
              <span className="eyebrow">Website</span>
              <div className="value" style={{ fontSize: 15 }}>
                {company.websiteUrl === undefined ? (
                  <span className="muted">Not stated</span>
                ) : (
                  <a href={company.websiteUrl} rel="nofollow noopener external" target="_blank">
                    {company.websiteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                  </a>
                )}
              </div>
            </div>
            <div className="stat">
              <span className="eyebrow">Careers page</span>
              <div className="value" style={{ fontSize: 15 }}>
                {company.careersUrl === undefined ? (
                  <span className="muted">Not stated</span>
                ) : (
                  <a href={company.careersUrl} rel="nofollow noopener external" target="_blank">
                    All roles →
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="section is-last">
        <div className="inner">
          <div className="section-heading">
            <div>
              <h2>Live listings</h2>
              <p>
                Ordered by the total paid on each. Each listing’s board positions are on its own
                page.
              </p>
            </div>
          </div>

          {jobs.length === 0 ? (
            <EmptyState headline={`${company.name} has no live listings.`}>
              Expired and removed listings are not shown.
            </EmptyState>
          ) : (
            <div className="card board">
              <Leaderboard entries={jobs} ranked={false} />
            </div>
          )}
        </div>
      </section>
    </>
  );
}
